import { useEditor } from "@grapesjs/react";
import { BootstrapSizes, Box, Button, ButtonColors, LightTable, Modal } from "@talentech/components";
import { useEffect, useState } from "react";
import { CustomCommands } from "@/shared/utils";
import { useGetPagesQuery, Template, Revision, Templates } from "@/redux/reducer/pages.api";
import { useParams } from "react-router-dom";
import { PageTypes } from "@/App";
import { service } from "@/service/service";
import { upgradeStandardButtons } from "../utils/standardTemplateButtons";

const RevisionModal: React.FC = () => {
  const editor = useEditor();
  const { type } = useParams<{ type: PageTypes }>() ?? { type: PageTypes.JOB };
  const [showModal, setShowModal] = useState(false);
  const [pageId, setPageId] = useState<string | undefined>(undefined);

  const { data: templatesData } = useGetPagesQuery({});

  const templateKey = `${type}_templates` as keyof Templates;
  const currentTemplate = templatesData?.[templateKey]?.find((t: Template) => t.id === pageId);

  const revisions = (currentTemplate?.html_draft_revision_urls || []).map((htmlRev: Revision) => {
    const configRev = currentTemplate?.config_draft_revision_urls.find(
      (c: Revision) => c.date === htmlRev.date
    );
    return { ...htmlRev, config_url: configRev?.url };
  }).filter((rev) => rev.config_url).sort(
    (a: Revision & {config_url?: string}, b: Revision & {config_url?: string}) => {
      const fixDate = (date: string) => date.replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3Z');
      const timeA = new Date(fixDate(a.date)).getTime();
      const timeB = new Date(fixDate(b.date)).getTime();
      if (isNaN(timeA) && isNaN(timeB)) return 0;
      if (isNaN(timeA)) return 1;
      if (isNaN(timeB)) return -1;
      return timeB - timeA;
    }
  );

  useEffect(() => {
    editor.Commands.add(CustomCommands.MODAL_REVISIONS_OPEN, (_editor, _sender, opts: { pageId?: string }) => {
      setPageId(opts.pageId);
      setShowModal(true);
    });
    editor.Commands.add(CustomCommands.MODAL_REVISIONS_CLOSE, () => setShowModal(false));
  }, [editor.Commands]);

  const columns = [
    {
      label: "Time",
      accessor: "date",
      Component: ({ date }: { date: string }) => {
        const fixedDate = date.replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3Z');
        const dateObj = new Date(fixedDate);
        return <>{dateObj.toLocaleString()}</>;
      },
    },
    {
      label: "Actions",
      Component: ({ url, config_url }: { url: string; config_url?: string }) => (
        <Box className="d-flex flex-row gap-2">
          <Button
            label="Preview"
            color={ButtonColors.PRIMARY}
            size={BootstrapSizes.SMALL}
            outline
            onClick={() => window.open(url, "_blank")}
          />
          <Button
            label="Rollback"
            size={BootstrapSizes.SMALL}
            color={ButtonColors.WARNING}
            outline
            onClick={async () => {
              if (config_url) {
                await service.fetchTemplate(config_url).then(async (res) => {
                  const {fonts, ...rest} = res;

                  editor.clearDirtyCount();
                  await editor.loadProjectData(upgradeStandardButtons(rest));
                }).then(() => {
                  editor.runCommand(CustomCommands.MODAL_REVISIONS_CLOSE);
                });
              }
            }}
          />
        </Box>
      ),
    },
  ];

  return (
    <Modal
      title="Revisions"
      showModal={showModal}
      onClose={() => editor.runCommand(CustomCommands.MODAL_REVISIONS_CLOSE)}
      size={BootstrapSizes.LARGE}
      buttons={[
        <Button
          key="close"
          label="Close"
          onClick={() => editor.runCommand(CustomCommands.MODAL_REVISIONS_CLOSE)}
          outline
        />,
      ]}
    >
      {revisions.length > 0 ? (
        <LightTable columns={columns} data={revisions} />
      ) : (
        <p>No revisions available.</p>
      )}
    </Modal>
  );
};

export default RevisionModal; 